import { Injectable, signal } from '@angular/core';
import { catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';

import { AuthService } from './auth.service';
import { SessionService } from './session.service';

@Injectable({ providedIn: 'root' })
export class SessionExpiryWarningService {
  private static readonly COUNTDOWN_START_MS = 60_000;
  private static readonly MAX_TIMEOUT_MS = 2_147_483_647;

  private countdownStartTimer: ReturnType<typeof setTimeout> | null = null;
  private logoutTimer: ReturnType<typeof setTimeout> | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private expiresAtMs: number | null = null;
  private started = false;
  private loggingOut = false;
  readonly remainingSeconds = signal<number | null>(null);

  constructor(
    private readonly auth: AuthService,
    private readonly session: SessionService,
  ) {}

  start(): void {
    this.started = true;
    this.reschedule();
  }

  stop(): void {
    this.started = false;
    this.clearTimers();
    this.remainingSeconds.set(null);
  }

  onTokenPairUpdated(): void {
    if (!this.started) {
      return;
    }
    this.reschedule();
  }

  extendSession(): void {
    this.auth.refresh().subscribe({
      next: (tokens) => {
        this.session.setTokenPair(tokens);
        this.onTokenPairUpdated();
      },
      error: () => {
        this.forceLogout('refresh_error_on_extend_session');
      },
    });
  }

  private reschedule(): void {
    this.clearTimers();
    this.remainingSeconds.set(null);

    const expiresAtMs = this.session.getSessionExpiresAtMs();
    if (!expiresAtMs) {
      return;
    }

    this.expiresAtMs = expiresAtMs;
    const now = Date.now();
    const remainingMs = expiresAtMs - now;
    if (remainingMs <= 0) {
      this.forceLogout('session_already_expired_on_reschedule');
      return;
    }

    if (remainingMs > SessionExpiryWarningService.MAX_TIMEOUT_MS) {
      const wakeDelayMs = Math.min(
        Math.max(1_000, remainingMs - SessionExpiryWarningService.COUNTDOWN_START_MS),
        SessionExpiryWarningService.MAX_TIMEOUT_MS,
      );
      this.debugLog('remainingMs exceeds setTimeout max -> schedule resync', {
        remainingMs,
        wakeDelayMs,
      });
      this.logoutTimer = setTimeout(() => {
        this.reschedule();
      }, wakeDelayMs);
      return;
    }

    const countdownDelayMs = Math.max(0, remainingMs - SessionExpiryWarningService.COUNTDOWN_START_MS);
    this.countdownStartTimer = setTimeout(() => {
      this.startCountdownLogs();
    }, countdownDelayMs);

    this.logoutTimer = setTimeout(() => {
      this.forceLogout('session_expired_timer');
    }, remainingMs);
  }

  private forceLogout(reason: string): void {
    this.debugLog('forceLogout requested', { reason, loggingOut: this.loggingOut });
    if (this.loggingOut) {
      return;
    }
    this.loggingOut = true;
    this.clearTimers();

    this.auth.logout().pipe(
      catchError(() => of(null)),
    ).subscribe({
      next: () => {
        this.debugLog('auth.logout completed', { reason });
        this.session.clearSession();
        this.redirectToLogin();
      },
      error: () => {
        this.debugLog('auth.logout errored', { reason });
        this.session.clearSession();
        this.redirectToLogin();
      },
      complete: () => {
        this.loggingOut = false;
      },
    });
  }

  private clearTimers(): void {
    if (this.countdownStartTimer) {
      clearTimeout(this.countdownStartTimer);
      this.countdownStartTimer = null;
    }
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
    }
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    this.expiresAtMs = null;
  }

  private startCountdownLogs(): void {
    this.logCountdown();
    this.countdownTimer = setInterval(() => {
      this.logCountdown();
    }, 1000);
  }

  private logCountdown(): void {
    if (!this.expiresAtMs) {
      return;
    }
    const remainingMs = this.expiresAtMs - Date.now();
    const remainingSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    this.remainingSeconds.set(remainingSeconds);
  }

  private redirectToLogin(): void {
    this.debugLog('redirectToLogin');
    if (typeof window !== 'undefined') {
      window.location.replace('/login');
    }
  }

  private debugLog(message: string, payload?: unknown): void {
    if (environment.production) {
      return;
    }
    console.warn(`[SessionExpiryWarningService] ${message}`, payload ?? null);
  }
}
